import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Tooltip } from './Tooltip'

type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  /** Required: icon-only buttons must have an accessible name. */
  label: string
  /** Show the label (or custom content) as a tooltip on hover/focus. Default true. */
  tooltip?: boolean | ReactNode
  size?: 'xs' | 'sm' | 'md'
  variant?: 'ghost' | 'primary' | 'outline' | 'subtle'
  active?: boolean
}

const sizes = {
  xs: 'size-7 rounded-full [&_svg]:size-3.5',
  sm: 'size-8 rounded-full [&_svg]:size-4',
  md: 'size-9 rounded-full [&_svg]:size-[18px]',
}

const variants = {
  ghost: 'text-ink-muted hover:bg-subtle hover:text-ink',
  subtle: 'bg-subtle text-ink-muted hover:bg-line/70 hover:text-ink',
  outline: 'bg-surface text-ink-muted shadow-card hover:bg-subtle hover:text-ink',
  primary: 'bg-accent text-accent-ink shadow-[0_1px_2px_rgb(0_0_0/0.15)] hover:bg-accent-hover',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tooltip = true, size = 'sm', variant = 'ghost', active, className, type = 'button', children, ...props },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(
        'pressable inline-flex shrink-0 items-center justify-center',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:pointer-events-none disabled:opacity-40',
        sizes[size],
        variants[variant],
        active && 'bg-subtle text-ink',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
  if (!tooltip) return button
  return <Tooltip content={tooltip === true ? label : tooltip}>{button}</Tooltip>
})
